import React, {
  memo,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import { Redirect } from "react-router-dom";
import axios from "axios";
import {
  FaKey,
  FaSearch,
  FaSyncAlt,
  FaUserGraduate,
  FaCopy,
  FaWhatsapp,
  FaEye,
  FaEyeSlash,
  FaMobileAlt,
} from "react-icons/fa";
import MyLoader from "../Spinner/MyLoader";
import SessionManager from "../Utils/SessionManager";
import myToast from "../Utils/MyToast";
import SelectClass from "../ChildComponent/SelectClass";
import BackButton from "../ChildComponent/BackButton";
import COLORS from "../resources/colors/COLORS";
import "../css/Admin_css/admin-customize.css";
import "../css/Admin_css/payroll.css";
import "../css/Admin_css/admission-dashboard.css";

const API_BASE = () =>
  `${process.env.REACT_APP_SERVER_NAME}${process.env.REACT_APP_TARGET_DIR}`;

const HERO_GRADIENT = `linear-gradient(135deg, ${COLORS.primary_color_shade5} 0%, ${COLORS.primary_color_shade7} 55%, ${COLORS.primary_color_shade9} 100%)`;
const EDUOK_DOWNLOAD = "https://eduokapp.com/download";

function studentInitials(name) {
  const parts = String(name || "?")
    .trim()
    .split(/\s+/)
    .filter(Boolean);
  if (parts.length >= 2) {
    return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
  }
  return (parts[0]?.[0] || "?").toUpperCase();
}

function getClassLabel(item) {
  const cls = Array.isArray(item?.class) ? item.class[0] : item?.class;
  if (!cls) return "";
  if (cls.section_id === "No" || !cls.section_name) {
    return cls.class_name || "";
  }
  return `${cls.class_name || ""}:${cls.section_name || ""}`;
}

function getContactNumber(item) {
  const fromDetails = item?.contact_details?.[0]?.contact_number;
  const raw = fromDetails || item?.contact_number || "";
  const digits = String(raw).replace(/\D/g, "");
  if (!digits) return "";
  if (digits.length === 10) return `91${digits}`;
  return digits;
}

function getDisplayMobile(item) {
  const fromDetails = item?.contact_details?.[0]?.contact_number;
  return String(fromDetails || item?.contact_number || "").trim();
}

function buildWhatsAppUrl(item) {
  const phone = getContactNumber(item);
  const name = encodeURIComponent(item?.stu_name || "Parent");
  const userId = encodeURIComponent(item?.student_enrollment_no || "");
  const password = encodeURIComponent(item?.password || "");
  const link = encodeURIComponent(EDUOK_DOWNLOAD);
  const text = `Hello%20${name},%20your%20EduOK%20login%20details%20are%3A%0AUser%20ID%3A%20${userId}%0APassword%3A%20${password}%0ADownload%20app%3A%20${link}`;
  if (phone) {
    return `https://wa.me/${phone}?text=${text}`;
  }
  return `https://wa.me/?text=${text}`;
}

async function copyText(label, value) {
  const text = String(value ?? "").trim();
  if (!text) {
    myToast.notify_warning(`${label} is empty.`);
    return;
  }
  try {
    if (navigator?.clipboard?.writeText) {
      await navigator.clipboard.writeText(text);
    } else {
      const el = document.createElement("textarea");
      el.value = text;
      el.setAttribute("readonly", "");
      el.style.position = "absolute";
      el.style.left = "-9999px";
      document.body.appendChild(el);
      el.select();
      document.execCommand("copy");
      document.body.removeChild(el);
    }
    myToast.notify_success(`${label} copied.`);
  } catch (err) {
    console.error("[StudentPassword] copy failed", err);
    myToast.notify_warning(`Could not copy ${label.toLowerCase()}.`);
  }
}

const StudentAvatar = memo(function StudentAvatar({ name, imagePath }) {
  const [imgFailed, setImgFailed] = useState(false);

  useEffect(() => {
    setImgFailed(false);
  }, [imagePath]);

  const showImg = Boolean(imagePath) && !imgFailed;

  return (
    <div className="rn-row__avatar" aria-hidden>
      {showImg ? (
        <img src={imagePath} alt="" onError={() => setImgFailed(true)} />
      ) : (
        <span className="rn-row__initials">{studentInitials(name)}</span>
      )}
    </div>
  );
});

const CredField = memo(function CredField({
  label,
  value,
  masked,
  onToggleMask,
  onCopy,
}) {
  const display = masked ? "••••••••" : value || "—";
  return (
    <div className="spw-cred">
      <span className="spw-cred__label">{label}</span>
      <div className="spw-cred__row">
        <code className="spw-cred__value" title={masked ? undefined : value}>
          {display}
        </code>
        <div className="spw-cred__actions">
          {typeof onToggleMask === "function" ? (
            <button
              type="button"
              className="spw-icon-btn"
              onClick={onToggleMask}
              aria-label={masked ? "Show password" : "Hide password"}
              title={masked ? "Show" : "Hide"}
            >
              {masked ? <FaEye aria-hidden /> : <FaEyeSlash aria-hidden />}
            </button>
          ) : null}
          <button
            type="button"
            className="spw-icon-btn"
            onClick={onCopy}
            aria-label={`Copy ${label}`}
            title="Copy"
            disabled={!value}
          >
            <FaCopy aria-hidden />
          </button>
        </div>
      </div>
    </div>
  );
});

const StudentPasswordRow = memo(function StudentPasswordRow({
  student,
  index,
  passwordVisible,
  onTogglePassword,
}) {
  const classLabel = getClassLabel(student);
  const mobile = getDisplayMobile(student);
  const waUrl = buildWhatsAppUrl(student);

  return (
    <div className="rn-row rn-row--password" style={{ "--rn-i": index }}>
      <div className="rn-row__who">
        <StudentAvatar
          name={student.stu_name}
          imagePath={student.student_img_path}
        />
        <div className="min-w-0">
          <div className="rn-row__name">{student.stu_name || "—"}</div>
          <div className="rn-row__chips">
            {student.father_name ? (
              <span className="rn-chip rn-chip--father">
                S/O {student.father_name}
              </span>
            ) : null}
            {classLabel ? (
              <span className="rn-chip rn-chip--class">{classLabel}</span>
            ) : null}
            {student.student_roll_no ? (
              <span className="rn-chip rn-chip--enroll">
                Roll: {student.student_roll_no}
              </span>
            ) : null}
            {student.sr_admission_no ? (
              <span className="rn-chip rn-chip--enroll">
                Adm: {student.sr_admission_no}
              </span>
            ) : null}
            {mobile ? (
              <span className="rn-chip rn-chip--enroll">
                <FaMobileAlt aria-hidden style={{ marginRight: 4 }} />
                {mobile}
              </span>
            ) : null}
          </div>
        </div>
      </div>

      <div className="spw-creds">
        <CredField
          label="User ID"
          value={student.student_enrollment_no}
          onCopy={() => copyText("User ID", student.student_enrollment_no)}
        />
        <CredField
          label="Password"
          value={student.password}
          masked={!passwordVisible}
          onToggleMask={onTogglePassword}
          onCopy={() => copyText("Password", student.password)}
        />
      </div>

      <div className="spw-share">
        <a
          href={waUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="spw-wa-btn"
          title={mobile ? `WhatsApp ${mobile}` : "Share via WhatsApp"}
        >
          <FaWhatsapp aria-hidden />
          <span>WhatsApp</span>
        </a>
      </div>
    </div>
  );
});

const StudentPassword = () => {
  const [loggedIn] = useState(
    () => localStorage.getItem("token") !== null
  );
  const [loadClass, setLoadClass] = useState(false);
  const [loading, setLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [sessionId, setSessionId] = useState("");
  const [classId, setClassId] = useState("");
  const [sectionId, setSectionId] = useState("");
  const [selectedClassPosition, setSelectedClassPosition] = useState(0);
  const [classSectionName, setClassSectionName] = useState("");
  const [students, setStudents] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [visiblePasswords, setVisiblePasswords] = useState({});

  useEffect(() => {
    setLoadClass(true);
  }, []);

  const onClassSelect = useCallback(
    (class_id, section_id, session_id, selected_position, class_name, section_name) => {
      const label =
        section_id === "No" ? class_name : `${class_name}:${section_name}`;
      setSectionId(section_id);
      setSessionId(session_id);
      setClassSectionName(label || "");
      setSelectedClassPosition(selected_position);
      setClassId(class_id);
      setSearchTerm("");
      setVisiblePasswords({});
    },
    []
  );

  const loadStudents = useCallback(
    async ({ soft = false } = {}) => {
      const schoolCode = SessionManager.getSchoolCode();
      if (!schoolCode || !classId || !sectionId || !sessionId) {
        setStudents([]);
        return;
      }

      if (soft) setRefreshing(true);
      else setLoading(true);

      try {
        const res = await axios.get(`${API_BASE()}getStudentAuthCredentials.php`, {
          params: {
            school_code: schoolCode,
            class_id: classId,
            section_id: sectionId,
            session_id: sessionId,
          },
        });
        const payload = res?.data;
        if (payload?.api_status === "OK" && Array.isArray(payload.data)) {
          setStudents(payload.data);
        } else {
          setStudents([]);
          if (payload?.api_remark && payload.api_status !== "ERROR") {
            myToast.notify_warning(payload.api_remark);
          }
        }
      } catch (error) {
        console.error("[StudentPassword] load error", error);
        setStudents([]);
        myToast.notify_warning("Failed to load student credentials.");
      } finally {
        setLoading(false);
        setRefreshing(false);
      }
    },
    [classId, sectionId, sessionId]
  );

  useEffect(() => {
    if (classId && sectionId && sessionId) {
      loadStudents();
    } else {
      setStudents([]);
    }
  }, [classId, sectionId, sessionId, loadStudents]);

  const filteredStudents = useMemo(() => {
    const q = searchTerm.trim().toLowerCase();
    if (!q) return students;
    return students.filter((s) => {
      const hay = [
        s.stu_name,
        s.father_name,
        s.student_enrollment_no,
        s.student_roll_no,
        s.sr_admission_no,
        s.password,
        getDisplayMobile(s),
        getClassLabel(s),
      ]
        .map((v) => String(v ?? "").toLowerCase())
        .join(" ");
      return hay.includes(q);
    });
  }, [students, searchTerm]);

  const togglePassword = useCallback((key) => {
    setVisiblePasswords((prev) => ({ ...prev, [key]: !prev[key] }));
  }, []);

  const rowKey = useCallback((student, index) => {
    return (
      student.student_enrollment_no ||
      student.sr_admission_no ||
      student.id ||
      `row-${index}`
    );
  }, []);

  if (!loggedIn) {
    return <Redirect to="/Login" />;
  }

  const classSelected = Boolean(classId && sectionId && sessionId);
  const busy = loading || refreshing;

  return (
    <div
      className="content payroll-page sm-page rn-page"
      style={{
        background: COLORS.white,
        fontFamily:
          'Inter, system-ui, -apple-system, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
      }}
    >
      <div
        className="content-header"
        style={{ background: COLORS.primary_color_shade5 }}
      >
        <div className="leftside-content-header">
          <ul className="breadcrumbs">
            <li>
              <i
                className="fa fa-home"
                aria-hidden="true"
                style={{ color: COLORS.white }}
              />
              <span style={{ color: COLORS.white }}> Student Password</span>
            </li>
          </ul>
        </div>
        <div className="rightside-content-header">
          <BackButton />
        </div>
      </div>

      <div className="bg_page_white_rounded box_shadow animated fadeInUp sm-shell">
        <header className="sm-hero" style={{ "--sm-hero-bg": HERO_GRADIENT }}>
          <div className="sm-hero__brand">
            <div className="sm-hero__icon" aria-hidden>
              <FaKey />
            </div>
            <div className="min-w-0">
              <h1 className="sm-hero__title">Student Password</h1>
              <p className="sm-hero__sub">
                {classSectionName
                  ? `${classSectionName} · ${students.length} student${
                      students.length === 1 ? "" : "s"
                    }`
                  : "Select a class to view login credentials"}
              </p>
            </div>
          </div>

          <div className="sm-hero__tools">
            <div className="sm-hero__search-group">
              <FaSearch className="rn-search-icon" aria-hidden />
              <input
                type="search"
                className="sm-hero__search"
                placeholder="Search students…"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                aria-label="Search students"
                disabled={!classSelected}
              />
            </div>
            <button
              type="button"
              className={`pp-icon-btn${refreshing ? " spinning" : ""}`}
              onClick={() => loadStudents({ soft: true })}
              disabled={busy || !classSelected}
              aria-label="Refresh list"
              title="Refresh"
            >
              <FaSyncAlt aria-hidden />
            </button>
          </div>
        </header>

        <div className="rn-content">
          {loadClass ? (
            <div className="rn-class-wrap">
              <SelectClass
                value={loadClass}
                action={onClassSelect}
                selected_position={selectedClassPosition}
              />
            </div>
          ) : null}

          {!classSelected ? (
            <div className="sm-empty">
              <FaUserGraduate className="rn-empty-icon" aria-hidden />
              <p className="sm-empty__title">Select a class</p>
              <p className="sm-empty__hint">
                Choose class and section to load student passwords
              </p>
            </div>
          ) : loading ? (
            <div className="rn-loading">
              <MyLoader />
            </div>
          ) : (
            <>
              <div className="rn-toolbar rn-toolbar--top">
                <div className="rn-toolbar__status" aria-live="polite">
                  <span className="rn-status-chip rn-status-chip--students">
                    {filteredStudents.length}
                    {filteredStudents.length !== students.length
                      ? ` of ${students.length}`
                      : ""}{" "}
                    student
                    {filteredStudents.length === 1 ? "" : "s"}
                  </span>
                  <span className="rn-status-chip rn-status-chip--clean">
                    Credentials
                  </span>
                </div>
              </div>

              {filteredStudents.length === 0 ? (
                <div className="sm-empty">
                  <FaKey className="rn-empty-icon" aria-hidden />
                  <p className="sm-empty__title">
                    {students.length === 0
                      ? "No students found"
                      : "No matches"}
                  </p>
                  <p className="sm-empty__hint">
                    {students.length === 0
                      ? "This class has no active students"
                      : "Try a different search"}
                  </p>
                </div>
              ) : (
                <div className="rn-list">
                  {filteredStudents.map((student, index) => {
                    const key = rowKey(student, index);
                    return (
                      <StudentPasswordRow
                        key={key}
                        student={student}
                        index={index}
                        passwordVisible={Boolean(visiblePasswords[key])}
                        onTogglePassword={() => togglePassword(key)}
                      />
                    );
                  })}
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default StudentPassword;
